import { ComponentType, lazy, LazyExoticComponent } from 'react';

type LazyModule<T extends ComponentType<any>> = { default: T };
type LazyImporter<T extends ComponentType<any>> = () => Promise<LazyModule<T>>;

const CHUNK_REFRESH_KEY = 'scrubplan:chunk-load-refresh';
const CHUNK_REFRESH_COOLDOWN_MS = 15000;
const DEFAULT_RETRY_DELAYS_MS = [250, 1000];

const wait = (delayMs: number): Promise<void> =>
	new Promise((resolve) => window.setTimeout(resolve, delayMs));

export const isChunkLoadError = (error: any): boolean => {
	const message = String((error && error.message) || error || '');
	return (
		Boolean(error && error.name === 'ChunkLoadError') ||
		/Loading (?:CSS )?chunk [^ ]+ failed/i.test(message) ||
		/Failed to fetch dynamically imported module/i.test(message) ||
		/Importing a module script failed/i.test(message)
	);
};

const refreshForStaleChunk = (): boolean => {
	if (typeof window === 'undefined') return false;

	try {
		const now = Date.now();
		const previousValue = window.sessionStorage.getItem(CHUNK_REFRESH_KEY);
		const previous = previousValue ? JSON.parse(previousValue) : null;
		const location = window.location.href;

		if (
			previous &&
			previous.location === location &&
			typeof previous.requestedAt === 'number' &&
			now - previous.requestedAt < CHUNK_REFRESH_COOLDOWN_MS
		) {
			return false;
		}

		window.sessionStorage.setItem(
			CHUNK_REFRESH_KEY,
			JSON.stringify({ location, requestedAt: now }),
		);
		window.location.reload();
		return true;
	} catch (_error) {
		return false;
	}
};

export const loadLazyModuleWithRetry = async <T extends ComponentType<any>>(
	importer: LazyImporter<T>,
	retryDelaysMs: number[] = DEFAULT_RETRY_DELAYS_MS,
): Promise<LazyModule<T>> => {
	let lastError: any;

	for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
		try {
			return await importer();
		} catch (error) {
			lastError = error;
			if (!isChunkLoadError(error)) throw error;

			if (attempt < retryDelaysMs.length) {
				await wait(retryDelaysMs[attempt]);
			}
		}
	}

	if (refreshForStaleChunk()) {
		return new Promise<LazyModule<T>>(() => undefined);
	}

	throw lastError;
};

const lazyWithRetry = <T extends ComponentType<any>>(
	importer: LazyImporter<T>,
): LazyExoticComponent<T> => lazy(() => loadLazyModuleWithRetry(importer));

export default lazyWithRetry;
